import { isIOS, isSafari } from "react-device-detect";

/**
 * Web Push / Badging helpers shared by the registration service, the opt-in
 * banner and the settings row.
 *
 * Two platform rules drive the whole design:
 *
 * 1. iOS only delivers Web Push to a PWA that has been added to the Home Screen
 *    (Safari 16.4+). A page opened in a Safari tab cannot subscribe at all.
 * 2. `Notification.requestPermission()` must be called from a user gesture on
 *    iOS. Anything that awaits first — including Firebase's own `getToken()`,
 *    which prompts internally — is rejected. Hence the split between
 *    `canRegisterSilently()` (already granted, no prompt, safe from an effect)
 *    and `requestPermission()`, which must be called from an onPress handler.
 */

const hasWindow = typeof window !== "undefined";

export const PERMISSION_GRANTED = "granted";
export const PERMISSION_DENIED = "denied";
export const PERMISSION_DEFAULT = "default";
export const PERMISSION_UNSUPPORTED = "unsupported";

export const isPushSupported = () =>
	hasWindow &&
	"Notification" in window &&
	"serviceWorker" in navigator &&
	"PushManager" in window;

/** True once the PWA runs from the Home Screen / app shell rather than a tab. */
export const isStandalone = () =>
	hasWindow &&
	(window.matchMedia("(display-mode: standalone)").matches ||
		// Non-standard, but it is the only signal Safari exposes on iOS.
		window.navigator.standalone === true);

/**
 * iOS refuses to subscribe a page opened in a Safari tab: the user has to add
 * the PWA to the Home Screen first. Every other platform subscribes from a
 * plain tab.
 */
export const needsInstallForPush = () => isIOS && !isStandalone();

/**
 * WebKit only honours `Notification.requestPermission()` inside a user gesture —
 * Safari on macOS as much as every browser on iOS, which are all WebKit under
 * the hood. Chromium and Gecko still accept it from anywhere, so they keep the
 * automatic prompt they have always had.
 */
export const requiresUserGesture = () => isIOS || isSafari;

export const getPermission = () =>
	isPushSupported() ? Notification.permission : PERMISSION_UNSUPPORTED;

/** Permission already granted: a token can be fetched without prompting. */
export const canRegisterSilently = () =>
	isPushSupported() && Notification.permission === PERMISSION_GRANTED;

/**
 * Must be called synchronously from a user gesture, otherwise iOS rejects it.
 * Safari below 16 only implements the callback form while newer engines return
 * a promise, so both are wired up rather than sniffing the user agent.
 */
export const requestPermission = () =>
	new Promise(resolve => {
		if (!isPushSupported()) {
			resolve(PERMISSION_UNSUPPORTED);
			return;
		}

		let settled = false;
		const done = permission => {
			if (!settled) {
				settled = true;
				resolve(permission ?? Notification.permission);
			}
		};

		try {
			const result = Notification.requestPermission(done);
			if (result && typeof result.then === "function") {
				result.then(done, () => done(Notification.permission));
			}
		} catch (error) {
			done(Notification.permission);
		}
	});

/**
 * Badging API: installed PWAs on iOS 16.4+, Chrome desktop and Android. It is a
 * no-op everywhere else, and iOS additionally ignores it until the notification
 * permission has been granted.
 */
export const isBadgingSupported = () =>
	hasWindow && "setAppBadge" in navigator && "clearAppBadge" in navigator;

// Shared with `web/firebase-messaging-sw.js`, which increments the count while
// the app is closed. The Badging API has no getter, so the running total has to
// live somewhere both sides can reach; the Cache API is available in a window
// and in a worker alike. Keep the two names in sync.
const BADGE_CACHE = "hapicolibri-badge";
const BADGE_KEY = "/__badge_count__";

/**
 * Hands the worker the authoritative total. Without this the count the worker
 * kept while the app was closed would keep growing from its own stale base
 * after the messages have been read.
 */
const writeStoredBadge = async count => {
	if (typeof caches === "undefined") {
		return;
	}
	try {
		const cache = await caches.open(BADGE_CACHE);
		await cache.put(BADGE_KEY, new Response(String(count)));
	} catch (error) {
		// Storage can be unavailable (private mode, blocked site data); the badge
		// still gets set below, it just will not survive the app being closed.
	}
};

export const setBadge = async count => {
	const normalized = Number.isInteger(count) && count > 0 ? count : 0;
	await writeStoredBadge(normalized);

	if (!isBadgingSupported()) {
		return;
	}
	try {
		if (normalized > 0) {
			await navigator.setAppBadge(normalized);
		} else {
			await navigator.clearAppBadge();
		}
	} catch (error) {
		// Browsers reject when the document is not installed, and Safari rejects
		// while the permission is still `default`. Neither is recoverable and
		// neither should surface to the user.
	}
};

export const clearBadge = () => setBadge(0);
